import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import type { Notification } from '@algoboo/schema/api'
import { Button, Card, EmptyState, Pill, Segmented, Skeleton, cx, fullTime, timeAgo } from '@algoboo/ui'
import { invalidate, notificationsQuery, useApiMutation, useNotifications } from '../lib/api'
import { notificationHref } from '../lib/notifications'
import { usePageMeta } from '../lib/meta'
import { PageHeader } from '../components/Shell'
import { SignInPrompt, useSession } from '../components/session'
import { QueryError } from '../components/widgets'
import { NotificationPreferences } from '../components/NotificationPreferences'
import { prefetchSignedIn } from '../lib/route-data'

export const Route = createFileRoute('/me/notifications')({
  validateSearch: z.object({ unread: z.boolean().optional(), view: z.enum(['delivery']).optional() }),
  loaderDeps: ({ search }) => ({ unread: search.unread === true }),
  loader: ({ context: { queryClient: qc }, deps }) => prefetchSignedIn(qc, () => [qc.ensureInfiniteQueryData(notificationsQuery(deps.unread))]),
  component: NotificationsPage,
})

function NotificationsPage() {
  usePageMeta('Notifications')
  const { me, loading } = useSession()
  const search = Route.useSearch()
  const unread = search.unread === true
  const delivery = search.view === 'delivery'
  const navigate = useNavigate({ from: '/me/notifications' })
  const q = useNotifications(unread, !!me)
  const mark = useApiMutation('markMyNotificationsRead', { invalidate: () => invalidate.notifications() })
  if (loading) return <Skeleton className="h-64 rounded-xl" />
  if (!me) return <SignInPrompt title="Notifications" body="Executed and refused trades, expiring permissions, lapsed plans, claimable rewards, and mentions and replies in your rooms. Sign in to see yours." />
  const items = q.data?.pages.flatMap((p) => p.items) ?? []
  const anyUnread = items.some((n) => !n.readAt)
  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Notifications"
        actions={
          !delivery && <Button variant="ghost" size="sm" icon="check" disabled={!anyUnread} loading={mark.isPending} onClick={() => mark.mutate({ body: { all: true } })}>
            Mark all read
          </Button>
        }
      />
      <Segmented<'all' | 'unread' | 'delivery'>
        label="Show"
        size="sm"
        className="mb-5"
        value={delivery ? 'delivery' : unread ? 'unread' : 'all'}
        onChange={(v) => void navigate({ search: v === 'unread' ? { unread: true } : v === 'delivery' ? { view: 'delivery' } : {}, replace: true })}
        options={[
          { value: 'all', label: 'All' },
          { value: 'unread', label: 'Unread' },
          { value: 'delivery', label: 'Delivery' },
        ]}
      />
      {delivery ? (
        <NotificationPreferences />
      ) : q.isLoading ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : q.error ? (
        <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
      ) : items.length === 0 ? (
        <EmptyState title={unread ? 'Nothing unread' : 'No notifications yet'} body="Trades, refusals, expiring permissions and mentions in your rooms show up here as they happen." />
      ) : (
        <Card className="overflow-hidden">
          <ol className="divide-y divide-line" data-testid="notifications">
            {items.map((n) => (
              <NotificationRow key={n.id} n={n} onRead={() => !n.readAt && mark.mutate({ body: { ids: [n.id] } })} />
            ))}
          </ol>
          {q.hasNextPage && (
            <div className="flex justify-center border-t border-line p-3">
              <Button variant="ghost" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                Load more
              </Button>
            </div>
          )}
        </Card>
      )}
    </>
  )
}

/** The room kinds name what happened in a room (lane CHATWEB1). */
const ROOM_KIND_WORDS: Partial<Record<Notification['kind'], string>> = {
  room_mention: 'Mention',
  room_reply: 'Reply',
  room_invite: 'Invite',
  room_pin: 'Pinned',
  room_dm: 'Direct message',
  room_digest: 'Muted rooms',
}

function NotificationRow({ n, onRead }: { n: Notification; onRead: () => void }) {
  const href = notificationHref(n)
  const body = (
    <>
      <span aria-hidden="true" className={cx('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-accent')} />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className={cx('text-[14px]', n.readAt ? 'text-ink-2' : 'font-semibold text-ink')}>{n.title}</span>
          {n.agent && <span className="font-mono text-[11.5px] text-ink-3">@{n.agent.handle}</span>}
          {ROOM_KIND_WORDS[n.kind] && (
            <Pill tone="sky" icon="chat">
              {ROOM_KIND_WORDS[n.kind]}
            </Pill>
          )}
        </span>
        {n.body && <span className="mt-0.5 block text-[13px] text-ink-3">{n.body}</span>}
      </span>
      <time dateTime={n.createdAt} title={fullTime(n.createdAt)} className="shrink-0 font-mono text-[11px] text-ink-4">
        {timeAgo(n.createdAt)}
      </time>
    </>
  )
  const cls = 'flex items-start gap-3 px-5 py-3.5 hover:bg-sunken'
  return (
    <li data-kind={n.kind} data-read={n.readAt ? 'true' : 'false'}>
      {href ? (
        <Link to={href.split('?')[0] as never} search={(href.includes('?') ? Object.fromEntries(new URLSearchParams(href.split('?')[1])) : undefined) as never} className={cls} onClick={onRead}>
          {body}
        </Link>
      ) : (
        <button type="button" className={cx(cls, 'w-full text-left')} onClick={onRead}>
          {body}
        </button>
      )}
    </li>
  )
}
